import { inject, Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { REQUEST } from '@personal-area/common';

enum MetaName {
  description = 'description',
  pragma = 'pragma',
  cacheControl = 'cache-control',
  robots = 'robots',
  ogUrl = 'og:url',
  ogTitle = 'og:title',
  ogDescription = 'og:description',
  ogImage = 'og:image',
  ogImageSecureUrl = 'og:image:secure_url',
  twitterUrl = 'twitter:url',
  twitterTitle = 'twitter:title',
  twitterDescription = 'twitter:description',
  twitterImage = 'twitter:image',
}

export class MetaTag {
  constructor(
    public name: MetaName,
    public value: string,
    public isFacebook: boolean,
  ) {
  }
}

@Injectable({ providedIn: 'root' })
export class MetaTagService {
  private readonly _host: string;

  constructor(
    private _meta: Meta,
    private _translateService: TranslateService,
  ) {
    const request = inject(REQUEST);
    this._host = `https://${request.hostname}/cabinet`;
  }

  public setSocialMediaTags(route: string, titleKey: string, descriptionKey: string, image: string): void {
    const title = this._translateService.instant(titleKey);
    const description = this._translateService.instant(descriptionKey);
    const imageUrl = `${this._host}/assets/img/meta/${image}`;
    const url = `${this._host}${route}`;
    const tags = [
      new MetaTag(MetaName.description, description, false),
      new MetaTag(MetaName.ogUrl, url, true),
      new MetaTag(MetaName.ogTitle, title, true),
      new MetaTag(MetaName.ogDescription, description, true),
      new MetaTag(MetaName.ogImage, imageUrl, true),
      new MetaTag(MetaName.ogImageSecureUrl, imageUrl, true),
      new MetaTag(MetaName.twitterUrl, url, false),
      new MetaTag(MetaName.twitterTitle, title, false),
      new MetaTag(MetaName.twitterDescription, description, false),
      new MetaTag(MetaName.twitterImage, imageUrl, false),
    ];

    this._setTags(tags);
  }

  public setRobotsTag(content: string): void {
    this._meta.updateTag({
      name: MetaName.robots,
      content: content,
    });
  }

  public setNoCacheTags(): void {
    this._meta.updateTag({
      httpEquiv: MetaName.pragma,
      content: 'no-cache',
    });
    this._meta.updateTag({
      httpEquiv: MetaName.cacheControl,
      content: 'no-cache',
    });
  }

  private _setTags(tags: MetaTag[]): void {
    tags.forEach(siteTag => {
      const attributeType = siteTag.isFacebook ? 'property' : 'name';

      this._meta.updateTag({
        [attributeType]: siteTag.name,
        content: siteTag.value,
      });
    });
  }
}
